import { Language } from '../enums/languages';

/**
 * Gets language override for specific domains
 * Some domains should always use a specific language regardless of browser settings
 * TODO: Make this configurable on fangroup instead of hardcoded
 */
export const getDomainLanguageOverride = (
  slug?: string | null
): string | null => {
  if (!slug) return null;

  // Plus domains should always use Dutch (Netherlands)
  // TODO: This should be configurable per fangroup
  const plusSlugs = ['plus-medewerkers', 'plus-b2b', 'plus-winactie'];
  if (plusSlugs.includes(slug)) {
    return Language.NL_NL;
  }

  return null;
};

/**
 * Maps browser language codes to our supported language variants
 * Handles Dutch variants (Belgium vs Netherlands) based on browser locale
 */
export const mapBrowserLanguageToSupported = (browserLang: string): string => {
  // Handle Dutch variants based on browser locale
  if (
    browserLang.toLowerCase().startsWith('nl_be') ||
    browserLang.toLowerCase().startsWith('nl-be')
  ) {
    return Language.NL_BE;
  }
  if (
    browserLang.toLowerCase().startsWith('nl_nl') ||
    browserLang.toLowerCase().startsWith('nl-nl')
  ) {
    return Language.NL_NL;
  }
  if (browserLang.toLowerCase().startsWith('nl')) {
    // Default Dutch to Netherlands variant if no country specified
    return Language.NL_NL;
  }

  // Handle other language mappings
  const langCode = browserLang.split('-')[0].toLowerCase();

  // Map to our supported languages
  const supportedLanguages = ['en', 'de', 'fr', 'it', 'es', 'ja', 'cn', 'ru'];
  if (supportedLanguages.includes(langCode)) {
    return langCode;
  }

  return Language.EN; // Default fallback
};

/**
 * Determines if browser detection should take precedence over stored preferences
 * This should happen when:
 * 1. No user preference is stored
 * 2. User hasn't manually selected a language (not from API)
 */
export const shouldUseBrowserDetection = (
  storedLanguage: string | null,
  userProfileLanguage?: string | null,
  hasUserExplicitlySelected?: boolean
): boolean => {
  // If no language is stored at all, use browser detection
  if (!storedLanguage) {
    return true;
  }

  // If user has explicitly selected a language in UI, respect that
  if (hasUserExplicitlySelected) {
    return false;
  }

  // If user profile from API has a language but it's generic 'nl',
  // allow browser detection to provide more specific variant
  if (userProfileLanguage === 'nl' && !hasUserExplicitlySelected) {
    return true;
  }

  return false;
};

/**
 * Gets the appropriate Dutch language variant based on user context
 * If user profile has 'nl', map to a specific variant based on browser or default to Belgium
 */
export const resolveDutchVariant = (
  userLanguage: string,
  browserLanguage?: string
): string => {
  if (userLanguage !== 'nl') {
    return userLanguage;
  }

  // If we have browser language info, use it to determine variant
  if (browserLanguage) {
    const mappedLanguage = mapBrowserLanguageToSupported(browserLanguage);
    if (
      mappedLanguage === Language.NL_BE ||
      mappedLanguage === Language.NL_NL
    ) {
      return mappedLanguage;
    }
  }

  // Default to Belgium variant for backward compatibility
  return Language.NL_BE;
};

/**
 * Normalizes language codes for consistent handling
 * Converts both nl_BE and nl_NL to 'nl' when needed for display purposes
 */
export const normalizeLanguageForDisplay = (language: string): string => {
  return language === Language.NL_NL || language === Language.NL_BE
    ? 'nl'
    : language;
};
